import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '~/store/config';
import * as actions from '~/store/actions';
import { ArchiveIcon, MarkAsReadIcon } from '~/assets/svgIcons';
import * as lib from '~/dependencies';
import { Conversation } from '~/interfaces/conversations';
import { log } from '~/helpers';
import { CircularProgress } from '@material-ui/core';
import { colors } from '~/layout';
import {
  Container,
  Button,
  Dialog,
  DialogContent,
  ActionButtons,
} from './styles';

enum ACTION_TYPE {
  ARCHIVE = 'ARCHIVE',
  MARK_AS_READ = 'MARK_AS_READ',
  CANCEL = 'CANCEL',
}

export default function BulkActions() {
  // STATES
  const [title, setTitle] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [type, setType] = useState<ACTION_TYPE | null>(null);

  // SELECTORS
  const conversations = useSelector(
    (state: RootState) => state.conversation?.conversations
  );
  const isLoading = useSelector(
    (state: RootState) => state.conversation?.isLoading
  );

  // CONST'S
  const selectedLng = conversations?.filter((e: Conversation) => e.selected)
    .length;

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  const handleMarkAsRead = () => {
    if (selectedLng <= 0) return;

    setTitle(t('screens.conversation.markAsReadTitle', { selectedLng }));
    setType(ACTION_TYPE.MARK_AS_READ);
    setOpenDialog(true);
  };

  const handleArchive = () => {
    if (selectedLng <= 0) return;

    setTitle(t('screens.conversation.archiveTitle', { selectedLng }));
    setType(ACTION_TYPE.ARCHIVE);
    setOpenDialog(true);
  };

  const handleCancel = () => {
    dispatch(actions.openConversationSelection(false));
  };

  const archiveConversations = async () => {
    try {
      await dispatch(actions.archiveConversations());
      dispatch(actions.openConversationSelection(false));
    } catch (err) {
      log.error(`Failure to archive conversations. Reason: ${err}`);
      setOpenDialog(false);
      dispatch(actions.setIsLoadingConversation(false));
      lib.ToastMessage('error', t('screens.conversation.archiveError'), 7000);
    }
  };

  const markConversationAsRead = async () => {
    try {
      dispatch(actions.markConversationAsRead());
      dispatch(actions.openConversationSelection(false));
      setOpenDialog(false);
    } catch (err) {
      log.error(`Failure to mark conversations as read. Reason: ${err}`);
      setOpenDialog(false);
      lib.ToastMessage(
        'error',
        t('screens.conversation.markAsReadError'),
        7000
      );
    }
  };

  const handleAction = async () => {
    if (type === ACTION_TYPE.ARCHIVE) {
      archiveConversations();
    } else {
      markConversationAsRead();
    }
  };

  const getButtonName = (typeValue: ACTION_TYPE | null) => {
    let buttonName = '';

    switch (typeValue) {
      case ACTION_TYPE.ARCHIVE:
        buttonName = t('screens.conversation.archive');
        break;
      case ACTION_TYPE.MARK_AS_READ:
        buttonName = t('screens.conversation.markAsRead');
        break;
      default:
    }

    return buttonName;
  };

  return (
    <Container>
      <Dialog open={openDialog} aria-labelledby="draggable-dialog-title">
        <DialogContent>
          <lib.ReactSVG
            src={type === ACTION_TYPE.ARCHIVE ? ArchiveIcon : MarkAsReadIcon}
          />
          <span>{title}</span>
          {isLoading ? (
            <CircularProgress
              variant="indeterminate"
              disableShrink
              size={20}
              thickness={4}
              style={{ marginTop: 4, color: colors.lightBlue13 }}
              color="inherit"
            />
          ) : null}
        </DialogContent>
        <ActionButtons>
          <Button
            type={ACTION_TYPE.CANCEL}
            onClick={() => setOpenDialog(false)}
            color={colors.darkGray05}>
            {t('screens.conversation.cancel')}
          </Button>
          <Button
            onClick={handleAction}
            color={
              type === ACTION_TYPE.ARCHIVE ? colors.lightBlue13 : colors.green07
            }>
            {getButtonName(type)}
          </Button>
        </ActionButtons>
      </Dialog>

      <Button onClick={handleCancel} color={colors.red02}>
        {t('screens.conversation.cancel')}
      </Button>
      <Button
        disabled={selectedLng <= 0}
        onClick={handleMarkAsRead}
        color={colors.lightBlue13}>
        {t('screens.conversation.markAsRead')}
      </Button>
      <Button
        disabled={selectedLng <= 0}
        onClick={handleArchive}
        color={colors.lightBlue13}>
        {t('screens.conversation.archive')}
      </Button>
    </Container>
  );
}
